import { resourcesApi, type Resource } from '../services/resources-api';

export class ReviewWorkflowView {
  private container: HTMLElement;
  private items: Resource[] = [];
  private currentIndex = 0;
  private total = 0;
  private isLoading = false;

  constructor() {
    const el = document.getElementById('review-view');
    if (!el) throw new Error('review-view element not found');
    this.container = el;
    this.setupKeyboardShortcuts();
  }

  async load(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    this.renderLoading();

    try {
      const result = await resourcesApi.pending(50);
      this.items = result.resources;
      this.total = result.total;
      this.currentIndex = 0;
      this.render();
    } catch (error) {
      this.renderError('Failed to load pending items');
    } finally {
      this.isLoading = false;
    }
  }

  private render(): void {
    if (this.items.length === 0) {
      this.renderEmptyState();
      return;
    }

    const item = this.items[this.currentIndex];
    const score = item.completionScore || 0;

    this.container.innerHTML = `
      <div class="review-workflow">
        <div class="review-header">
          <div class="review-header-left">
            <h2>Review Queue</h2>
            <span class="review-progress">${this.currentIndex + 1} of ${this.total} pending</span>
          </div>
          <div class="review-nav">
            <button class="btn-icon" id="prev-item" ${this.currentIndex === 0 ? 'disabled' : ''} title="Previous (←)">
              <span class="material-symbols-outlined">chevron_left</span>
            </button>
            <button class="btn-icon" id="next-item" ${this.currentIndex >= this.items.length - 1 ? 'disabled' : ''} title="Next (→)">
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        </div>

        <div class="review-card">
          <div class="review-card-header">
            <h3 class="review-title">${this.escapeHtml(item.title)}</h3>
            <div class="review-score" style="color: ${this.getScoreColor(score)}">
              ${score}%
            </div>
          </div>

          <div class="review-meta">
            <span class="review-meta-item">
              <span class="material-symbols-outlined">source</span>
              ${item.source || 'manual'}
            </span>
            <span class="review-meta-item">
              <span class="material-symbols-outlined">category</span>
              ${item.resourceCategory}
            </span>
            <span class="review-meta-item">
              <span class="material-symbols-outlined">schedule</span>
              ${this.formatDate(item.createdAt)}
            </span>
          </div>

          <div class="review-content">
            ${this.renderContent(item.content)}
          </div>

          <div class="review-fields">
            <div class="review-field">
              <label>Category</label>
              <select id="field-category">
                ${this.renderCategoryOptions(item.resourceCategory)}
              </select>
            </div>

            <div class="review-field">
              <label>Tags</label>
              <input type="text" id="field-tags" value="${(item.tags || []).join(', ')}" placeholder="tag1, tag2, tag3">
            </div>

            <div class="review-field">
              <label>Project</label>
              <input type="text" id="field-project" value="${item.project || ''}" placeholder="Optional project">
            </div>
          </div>
        </div>

        <div class="review-actions">
          <button class="review-btn trash" id="btn-trash" title="Delete permanently">
            <span class="material-symbols-outlined">delete</span>
            Trash (T)
          </button>
          <button class="review-btn wrong" id="btn-wrong" title="Wrong classification">
            <span class="material-symbols-outlined">close</span>
            Wrong (W)
          </button>
          <button class="review-btn skip" id="btn-skip" title="Skip for now">
            <span class="material-symbols-outlined">skip_next</span>
            Skip (S)
          </button>
          <button class="review-btn ok" id="btn-ok" title="Approve for training">
            <span class="material-symbols-outlined">check</span>
            OK (O)
          </button>
          <button class="review-btn gold" id="btn-gold" title="Perfect example">
            <span class="material-symbols-outlined">star</span>
            Gold (G)
          </button>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    this.container.querySelector('#btn-trash')?.addEventListener('click', () => this.review('trash'));
    this.container.querySelector('#btn-wrong')?.addEventListener('click', () => this.review('wrong'));
    this.container.querySelector('#btn-skip')?.addEventListener('click', () => this.next());
    this.container.querySelector('#btn-ok')?.addEventListener('click', () => this.review('ok'));
    this.container.querySelector('#btn-gold')?.addEventListener('click', () => this.review('gold'));
    this.container.querySelector('#prev-item')?.addEventListener('click', () => this.prev());
    this.container.querySelector('#next-item')?.addEventListener('click', () => this.next());
  }

  private setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.container.offsetParent) return; // Not visible
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;

      switch (e.key.toLowerCase()) {
        case 't': this.review('trash'); break;
        case 'w': this.review('wrong'); break;
        case 's': this.next(); break;
        case 'o': this.review('ok'); break;
        case 'g': this.review('gold'); break;
        case 'arrowleft': this.prev(); break;
        case 'arrowright': this.next(); break;
      }
    });
  }

  private async review(level: 'trash' | 'wrong' | 'ok' | 'gold'): Promise<void> {
    const item = this.items[this.currentIndex];
    if (!item) return;

    if (level === 'trash' && !confirm('Delete this item permanently?')) return;

    try {
      await resourcesApi.review(item.id, level);

      this.items.splice(this.currentIndex, 1);
      this.total--;

      if (this.currentIndex >= this.items.length) {
        this.currentIndex = Math.max(0, this.items.length - 1);
      }

      this.showToast(this.getReviewMessage(level));
      this.render();
    } catch (error) {
      this.showToast('Failed to update item');
    }
  }

  private getReviewMessage(level: string): string {
    const messages: Record<string, string> = {
      trash: 'Item deleted',
      wrong: 'Marked as wrong',
      ok: 'Approved for training',
      gold: 'Marked as gold example'
    };
    return messages[level] || 'Updated';
  }

  private next(): void {
    if (this.currentIndex < this.items.length - 1) {
      this.currentIndex++;
      this.render();
    }
  }

  private prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.render();
    }
  }

  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="review-loading">
        <span class="material-symbols-outlined spinning">progress_activity</span>
        <p>Loading pending items...</p>
      </div>
    `;
  }

  private renderEmptyState(): void {
    this.container.innerHTML = `
      <div class="review-empty">
        <span class="material-symbols-outlined">task_alt</span>
        <h3>All caught up!</h3>
        <p>No items pending review.</p>
      </div>
    `;
  }

  private renderError(message: string): void {
    this.container.innerHTML = `
      <div class="review-error">
        <span class="material-symbols-outlined">error</span>
        <h3>Error</h3>
        <p>${message}</p>
        <button class="btn btn-primary" id="btn-retry">Retry</button>
      </div>
    `;
    this.container.querySelector('#btn-retry')?.addEventListener('click', () => this.load());
  }

  private renderContent(content: string | null): string {
    if (!content) return '<p class="review-no-content">No content</p>';

    const truncated = content.length > 2000 ? content.slice(0, 2000) + '...' : content;
    return `<div class="review-content-text">${this.escapeHtml(truncated)}</div>`;
  }

  private renderCategoryOptions(selected: string): string {
    const categories = [
      'Content Creation', 'Business', 'Marketing', 'Paid Ads',
      'Desarrollo Personal', 'Programming', 'Video Editing',
      'AI Prompts', 'Uncategorized'
    ];

    return categories.map(cat =>
      `<option value="${cat}" ${cat === selected ? 'selected' : ''}>${cat}</option>`
    ).join('');
  }

  private getScoreColor(score: number): string {
    if (score >= 80) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  }

  private formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private showToast(message: string): void {
    const existing = document.querySelector('.review-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'review-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.remove(), 2000);
  }
}
